import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { 
  Wrench, Gauge, CircuitBoard, Disc, Cog, 
  Car, Zap, Wind, Droplet, Settings, Phone 
} from 'lucide-react';
import './Services.css';

const Services = () => {
  const services = [
    {
      icon: Gauge,
      title: 'Engine Diagnostics',
      description: 'Advanced computer diagnostics to quickly identify check engine lights, performance issues, and engine problems. We use the latest diagnostic equipment to pinpoint issues accurately.',
      features: ['Check engine light diagnosis', 'Performance troubleshooting', 'Computer scanning', 'Sensor testing']
    },
    {
      icon: Disc,
      title: 'Brake Service & Repair',
      description: 'Complete brake inspection, repair, and replacement services. From brake pads and rotors to calipers and brake lines, we keep your braking system safe and reliable.',
      features: ['Brake pad replacement', 'Rotor resurfacing/replacement', 'Brake fluid service', 'Caliper repair']
    },
    {
      icon: Cog,
      title: 'Suspension & Steering',
      description: 'Expert repair of suspension components, shocks, struts, and steering systems. We restore smooth handling and proper alignment for a comfortable, safe ride.',
      features: ['Shock & strut replacement', 'Tie rod repair', 'Ball joint replacement', 'Wheel alignment']
    },
    {
      icon: CircuitBoard,
      title: 'Electrical Systems',
      description: 'Diagnosis and repair of electrical issues including alternators, starters, batteries, and wiring. We solve electrical problems that keep your vehicle from starting or running properly.',
      features: ['Alternator replacement', 'Starter repair', 'Battery testing & replacement', 'Electrical troubleshooting']
    },
    {
      icon: Zap,
      title: 'Transmission Service',
      description: 'Transmission repair, maintenance, and replacement services. We provide honest assessments and quality work on both automatic and manual transmissions.',
      features: ['Transmission fluid service', 'Clutch replacement', 'Transmission repair', 'Transmission replacement']
    },
    {
      icon: Car,
      title: 'Body & Bumper Repair',
      description: 'Front-end repair, bumper work, and collision repair services. We restore your vehicle\'s appearance and structural integrity after accidents or damage.',
      features: ['Bumper repair & replacement', 'Front-end collision repair', 'Body panel repair', 'Paint touch-up']
    },
    {
      icon: Droplet,
      title: 'Cooling System Service',
      description: 'Radiator repair, coolant service, and cooling system diagnostics to prevent overheating. Keep your engine running at the proper temperature.',
      features: ['Radiator repair/replacement', 'Coolant flush', 'Thermostat replacement', 'Hose & belt service']
    },
    {
      icon: Wind,
      title: 'AC & Heating Service',
      description: 'Air conditioning repair, recharge, and heating system service. Stay comfortable year-round with properly functioning climate control.',
      features: ['AC recharge', 'Compressor repair', 'Heater core service', 'Climate control diagnosis']
    },
    {
      icon: Settings,
      title: 'General Maintenance',
      description: 'Oil changes, filter replacements, fluid services, and routine maintenance to keep your vehicle running smoothly and prevent costly repairs.',
      features: ['Oil changes', 'Filter replacement', 'Fluid checks & top-offs', 'Multi-point inspection']
    },
    {
      icon: Wrench,
      title: 'Engine Repair',
      description: 'Complete engine repair services from minor fixes to major overhauls. We handle timing belts, gaskets, seals, and complex engine problems.',
      features: ['Timing belt replacement', 'Gasket & seal repair', 'Head gasket service', 'Engine overhaul']
    }
  ];

  return (
    <div className="services-page">
      <Header />
      
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-background">
          <img 
            src="https://images.unsplash.com/photo-1771340012378-3c86cb649193" 
            alt="Auto repair services"
          />
          <div className="services-hero-overlay"></div>
        </div>
        <div className="container">
          <div className="services-hero-content">
            <h1 className="fade-in-up">Our Services</h1>
            <p className="services-hero-subtitle fade-in-up">
              Comprehensive auto repair services for all your vehicle needs
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section section-dark">
        <div className="container">
          <div className="all-services-grid">
            {services.map((service, index) => (
              <div key={index} className="full-service-card card">
                <div className="service-header">
                  <div className="service-icon-wrapper">
                    <service.icon size={32} className="service-icon" />
                  </div>
                  <h3>{service.title}</h3>
                </div>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="section section-light">
        <div className="container">
          <div className="service-benefits text-center">
            <h2>Why Choose Our Services</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>Honest Diagnostics</h3>
                <p>We'll tell you exactly what's wrong and what it will take to fix it—no guesswork, no upselling.</p>
              </div>
              <div className="benefit-item">
                <h3>Quality Parts</h3>
                <p>We use reliable parts and components that meet or exceed manufacturer specifications.</p>
              </div>
              <div className="benefit-item">
                <h3>Skilled Technicians</h3>
                <p>Our experienced team has the expertise to diagnose and repair a wide range of issues correctly.</p>
              </div>
              <div className="benefit-item">
                <h3>Fair Pricing</h3>
                <p>Competitive rates with transparent pricing. We provide clear estimates before starting work.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section section-dark">
        <div className="container">
          <div className="service-coverage text-center">
            <h2>Serving Vehicles of All Makes & Models</h2>
            <p className="section-subtitle mb-6">
              From domestic to foreign vehicles, cars to trucks and SUVs—we have the skills and equipment to service all types of vehicles.
            </p>
            <div className="vehicle-types">
              <span>Domestic Vehicles</span>
              <span>Import Vehicles</span>
              <span>Cars</span>
              <span>Trucks</span>
              <span>SUVs</span>
              <span>Vans</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="container">
          <div className="services-cta-content text-center">
            <h2>Need Auto Repair Service?</h2>
            <p>
              Call us today to schedule service or request a quote. We're here to help with honest advice and quality repairs.
            </p>
            <div className="services-cta-actions">
              <a href="tel:6517723388" className="btn-primary btn-large">
                <Phone size={20} />
                Call (651) 772-3388
              </a>
              <Link to="/contact" className="btn-secondary btn-large">
                Request Service
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
